/**
 * Importar activos fijos — el modal de tres pasos (ítem 126).
 *
 * ── POR QUE TRES PASOS Y NO UN BOTON DE SUBIR ───────────────────────────────
 *
 * Porque lo que importa de un archivo de trescientas filas no es que entren: es
 * saber **cuáles NO van a entrar y por qué**, antes de tocar la base. Un botón
 * que sube y avisa «se importaron 287 de 300» deja al usuario con trece filas
 * perdidas que no sabe cuáles son.
 *
 *   1 · El archivo, con la plantilla al lado y la fecha de corte.
 *   2 · Lo que se vio: válidos, inválidos con su motivo, duplicados con el suyo.
 *   3 · Lo que quedó, y el aviso si hubo que suponer algo.
 *
 * ── LA FECHA DE CORTE VA EN EL PASO 1, NO EN EL ARCHIVO ─────────────────────
 *
 * Es la fecha desde la que Finance se hace cargo de depreciar, y es **una para
 * toda la carga**. Por fila permitiría media flota con corte en junio y la otra
 * mitad en setiembre, y el gasto del semestre saldría de un sub-libro que
 * arranca en dos momentos distintos.
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  Upload, FileDown, X, CheckCircle2, AlertTriangle, Copy, ArrowRight, Loader2
} from 'lucide-react';
import { contabilidadApi } from '../../utils/api';

/** El primer día del mes que viene: el corte más común, y nunca uno pasado a medias. */
function proximoPrimero () {
  const h = new Date();
  const d = new Date(h.getFullYear(), h.getMonth() + 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

const plata = (n) => (n === null || n === undefined || n === ''
  ? '—'
  : Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

/** El mensaje del servidor es el que explica la regla; no se reemplaza por uno genérico. */
const avisar = (e, siNo) => toast.error(e?.response?.data?.error || e?.message || siNo);

function Pestanas ({ actual, cambiar, previa }) {
  const items = [
    { clave: 'validos', rotulo: 'Correctos', n: previa.validos.length, color: 'text-emerald-700 border-emerald-600' },
    { clave: 'invalidos', rotulo: 'Inválidos', n: previa.invalidos.length, color: 'text-red-700 border-red-600' },
    { clave: 'duplicados', rotulo: 'Duplicados', n: previa.duplicados.length, color: 'text-amber-700 border-amber-600' }
  ];
  return (
    <div className="flex gap-1 border-b">
      {items.map(i => (
        <button
          key={i.clave}
          onClick={() => cambiar(i.clave)}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            actual === i.clave ? i.color : 'text-gray-500 border-transparent hover:text-gray-700'}`}
        >
          {i.rotulo} <span className="tabular-nums">({i.n})</span>
        </button>
      ))}
    </div>
  );
}

export default function ImportarActivos ({ onCerrar, onImportado }) {
  const [paso, setPaso] = useState(1);
  const [archivo, setArchivo] = useState(null);
  const [fechaCorte, setFechaCorte] = useState(proximoPrimero());
  const [cargando, setCargando] = useState(false);
  const [previa, setPrevia] = useState(null);
  const [pestana, setPestana] = useState('validos');
  const [resultado, setResultado] = useState(null);

  const bajarPlantilla = async () => {
    try {
      const r = await contabilidadApi.getActivosPlantilla();
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'activos-fijos.csv';
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      avisar(e, 'No se pudo bajar la plantilla');
    }
  };

  const previsualizar = async () => {
    if (!archivo) { toast.error('Elija el archivo'); return; }
    if (!fechaCorte) { toast.error('Indique desde cuándo deprecia Finance'); return; }
    setCargando(true);
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      fd.append('fechaCorte', fechaCorte);
      const r = await contabilidadApi.previsualizarActivos(fd);
      if (r.data.error) { toast.error(r.data.error); return; }
      setPrevia(r.data);
      // Se abre en la pestaña que tiene algo que arreglar: si hay inválidos, lo
      // que el usuario necesita ver no es la lista de los que sí entran.
      setPestana(r.data.invalidos.length ? 'invalidos' : 'validos');
      setPaso(2);
    } catch (e) {
      avisar(e, 'No se pudo leer el archivo');
    } finally {
      setCargando(false);
    }
  };

  const confirmar = async () => {
    setCargando(true);
    try {
      const r = await contabilidadApi.confirmarActivos({ validos: previa.validos, fechaCorte });
      setResultado(r.data);
      setPaso(3);
      if (r.data.creados.length) onImportado?.();
    } catch (e) {
      avisar(e, 'No se pudo importar');
    } finally {
      setCargando(false);
    }
  };

  const bajarErrores = () => {
    const filas = [
      'Línea,Código,Motivo',
      ...previa.invalidos.map(f =>
        `${f.linea},"${f.datos?.codigo || ''}","${f.errores.join(' ').replace(/"/g, '""')}"`),
      ...previa.duplicados.map(f =>
        `${f.linea},"${f.datos?.codigo || ''}","${(f.motivo || '').replace(/"/g, '""')}"`)
    ].join('\n');
    // Con BOM: sin él Excel abre los acentos de los motivos como basura, y el
    // archivo de errores existe justamente para leerlos.
    const url = URL.createObjectURL(new Blob([`\uFEFF${filas}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'activos-no-importados.csv';
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h3 className="font-semibold text-lg">Importar activos fijos</h3>
            <p className="text-sm text-gray-500">
              Paso {paso} de 3 ·{' '}
              {paso === 1 ? 'el archivo' : paso === 2 ? 'lo que se va a importar' : 'resultado'}
            </p>
          </div>
          <button onClick={onCerrar} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* ── Paso 1 ─────────────────────────────────────────────────── */}
          {paso === 1 && (
            <div className="space-y-5">
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-900">
                <p className="font-medium mb-1">Los activos pueden venir ya depreciados.</p>
                <p>
                  Si un activo trae depreciación acumulada del sistema anterior, Finance
                  sigue desde donde venía: no lo vuelve a depreciar desde cero. Para eso
                  hacen falta la <strong>fecha de puesta en marcha</strong> de cada activo
                  y la <strong>fecha de corte</strong> de abajo.
                </p>
              </div>

              <div>
                <button
                  onClick={bajarPlantilla}
                  className="inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900 font-medium"
                >
                  <FileDown size={16} /> Descargar plantilla de ejemplo
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  Trae un activo de muestra ya depreciado. Los encabezados se reconocen
                  también con los nombres usuales (placa, costo, dep. acumulada…).
                </p>
              </div>

              <label className="block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                />
                <Upload className="mx-auto text-gray-400 mb-2" size={28} />
                <span className="text-sm text-gray-700">
                  {archivo ? archivo.name : 'Elija un archivo CSV o Excel'}
                </span>
              </label>

              <div>
                <label htmlFor="fecha-corte" className="block text-sm font-medium text-gray-700 mb-1">
                  Desde cuándo deprecia Finance <span className="text-red-600">*</span>
                </label>
                <input
                  id="fecha-corte"
                  type="date"
                  value={fechaCorte}
                  onChange={(e) => setFechaCorte(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Es una sola para toda la carga. Lo depreciado antes de esta fecha queda
                  registrado como arrastre del sistema anterior y nunca se contabiliza:
                  ese gasto ya está en el mayor.
                </p>
              </div>
            </div>
          )}

          {/* ── Paso 2 ─────────────────────────────────────────────────── */}
          {paso === 2 && previa && (
            <div className="space-y-4">
              {/* Qué columnas se leyeron y cuáles se ignoran. Sin esto, una
                  columna mal nombrada se pierde en silencio. */}
              <div className="text-xs text-gray-600">
                <span className="font-medium">Columnas del archivo: </span>
                {previa.columnas.map((c, i) => (
                  <span key={c.nombre} className={c.usada ? '' : 'line-through text-gray-400'}>
                    {c.nombre}{i < previa.columnas.length - 1 ? ', ' : ''}
                  </span>
                ))}
                {previa.columnas.some(c => !c.usada) && (
                  <span className="text-amber-700"> · las tachadas no se van a leer</span>
                )}
              </div>

              <Pestanas actual={pestana} cambiar={setPestana} previa={previa} />

              <div className="overflow-x-auto">
                {pestana === 'validos' && (
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Línea</th>
                        <th className="px-3 py-2 text-left font-medium">Código</th>
                        <th className="px-3 py-2 text-left font-medium">Nombre</th>
                        <th className="px-3 py-2 text-left font-medium">Clase</th>
                        <th className="px-3 py-2 text-right font-medium">Costo</th>
                        <th className="px-3 py-2 text-right font-medium">Dep. acumulada</th>
                        <th className="px-3 py-2 text-left font-medium">En marcha</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {previa.validos.map(f => (
                        <tr key={f.linea}>
                          <td className="px-3 py-2 text-gray-400 tabular-nums">{f.linea}</td>
                          <td className="px-3 py-2 font-medium">{f.datos.codigo}</td>
                          <td className="px-3 py-2">{f.datos.nombre}</td>
                          <td className="px-3 py-2 text-gray-600">{f.clase?.codigo}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{plata(f.datos.valorAdquisicion)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {plata(f.datos.depreciacionAcumulada)}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {f.datos.fechaPuestaEnMarcha || (
                              <span className="text-amber-700">obra en curso</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {!previa.validos.length && (
                        <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                          Ninguna fila está lista para importar.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                )}

                {pestana === 'invalidos' && (
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Línea</th>
                        <th className="px-3 py-2 text-left font-medium">Código</th>
                        <th className="px-3 py-2 text-left font-medium">Qué hay que arreglar</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {previa.invalidos.map(f => (
                        <tr key={f.linea}>
                          <td className="px-3 py-2 text-gray-400 tabular-nums">{f.linea}</td>
                          <td className="px-3 py-2 font-medium">{f.datos?.codigo || '—'}</td>
                          <td className="px-3 py-2 text-red-700">{f.errores.join(' ')}</td>
                        </tr>
                      ))}
                      {!previa.invalidos.length && (
                        <tr><td colSpan={3} className="px-3 py-6 text-center text-gray-500">
                          Ninguna fila con problemas.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                )}

                {pestana === 'duplicados' && (
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Línea</th>
                        <th className="px-3 py-2 text-left font-medium">Código</th>
                        <th className="px-3 py-2 text-left font-medium">Motivo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {previa.duplicados.map(f => (
                        <tr key={`${f.linea}-${f.datos?.codigo}`}>
                          <td className="px-3 py-2 text-gray-400 tabular-nums">{f.linea}</td>
                          <td className="px-3 py-2 font-medium">{f.datos?.codigo || '—'}</td>
                          <td className="px-3 py-2 text-amber-700">{f.motivo}</td>
                        </tr>
                      ))}
                      {!previa.duplicados.length && (
                        <tr><td colSpan={3} className="px-3 py-6 text-center text-gray-500">
                          Ningún código repetido.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {(previa.invalidos.length > 0 || previa.duplicados.length > 0) && (
                <button
                  onClick={bajarErrores}
                  className="inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900"
                >
                  <FileDown size={16} /> Bajar las filas que no van a entrar, con su motivo
                </button>
              )}
            </div>
          )}

          {/* ── Paso 3 ─────────────────────────────────────────────────── */}
          {paso === 3 && resultado && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-4">
                <CheckCircle2 className="text-emerald-600 mt-0.5 shrink-0" size={20} />
                <div className="text-sm text-emerald-900">
                  <p className="font-medium">
                    {resultado.creados.length} activo(s) importado(s).
                  </p>
                  {resultado.creados.some(c => c.enCurso) && (
                    <p className="mt-1">
                      {resultado.creados.filter(c => c.enCurso).length} entraron como obra en
                      curso: no van a depreciar hasta que se les ponga la fecha de puesta en marcha.
                    </p>
                  )}
                </div>
              </div>

              {resultado.aviso && (
                <div className="flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-200 p-4">
                  <AlertTriangle className="text-amber-600 mt-0.5 shrink-0" size={20} />
                  <p className="text-sm text-amber-900">{resultado.aviso}</p>
                </div>
              )}

              {resultado.errores.length > 0 && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                  <p className="text-sm font-medium text-red-900 mb-2">
                    {resultado.errores.length} fila(s) no entraron:
                  </p>
                  <ul className="text-sm text-red-800 space-y-1">
                    {resultado.errores.map(e => (
                      <li key={e.linea}>
                        Línea {e.linea} ({e.codigo}): {e.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t bg-gray-50 rounded-b-xl">
          <div className="text-sm text-gray-500">
            {paso === 2 && previa && (
              <span className="inline-flex items-center gap-1">
                <Copy size={14} /> {previa.total} fila(s) en el archivo
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {paso === 2 && (
              <button
                onClick={() => setPaso(1)}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-white"
              >
                Cambiar el archivo
              </button>
            )}
            {paso === 1 && (
              <button
                onClick={previsualizar}
                disabled={cargando || !archivo}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {cargando ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />}
                Ver qué va a entrar
              </button>
            )}
            {paso === 2 && (
              <button
                onClick={confirmar}
                disabled={cargando || !previa.validos.length}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {cargando ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
                Importar {previa.validos.length} activo(s)
              </button>
            )}
            {paso === 3 && (
              <button
                onClick={onCerrar}
                className="px-4 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-900"
              >
                Listo
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
